/**
 * Riga di un allegato, stile Notion: icona del tipo, nome, estensione e dimensione,
 * pulsanti Apri e Scarica sempre visibili (non solo al passaggio del mouse).
 * Usata sotto ogni nota e nell'elenco "Allegati" della scheda Lead/Cliente.
 */
import React from 'react';
import {
  Download,
  ExternalLink,
  FileArchive,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  File as FileIcon,
  Presentation,
  CornerDownRight,
} from 'lucide-react';
import { SignedUrl } from '../../shared/SignedUrl';
import { tip, iconTip } from '../../messages/ChatTooltip';
import {
  AttachmentKind,
  AttachmentLike,
  attachmentExtension,
  formatAttachmentSize,
  getAttachmentKind,
  withDownloadParam,
} from '../../../lib/utils/noteAttachmentFiles';

const KIND_STYLE: Record<AttachmentKind, { Icon: React.ElementType; cls: string }> = {
  image: { Icon: FileImage, cls: 'text-[#337EA9] bg-[rgba(51,126,169,0.12)]' },
  pdf: { Icon: FileText, cls: 'text-[#D44C47] bg-[rgba(212,76,71,0.12)]' },
  sheet: { Icon: FileSpreadsheet, cls: 'text-[#448361] bg-[rgba(68,131,97,0.12)]' },
  doc: { Icon: FileText, cls: 'text-[#337EA9] bg-[rgba(51,126,169,0.12)]' },
  slides: { Icon: Presentation, cls: 'text-[#D9730D] bg-[rgba(217,115,13,0.12)]' },
  archive: { Icon: FileArchive, cls: 'text-[#CB912F] bg-[rgba(203,145,47,0.12)]' },
  code: { Icon: FileCode, cls: 'text-[#9065B0] bg-[rgba(144,101,176,0.12)]' },
  other: { Icon: FileIcon, cls: 'text-[#787774] bg-[rgba(55,53,47,0.08)]' },
};

const ACTION_CLS =
  'inline-flex h-7 w-7 items-center justify-center rounded text-[#787774] hover:bg-[rgba(55,53,47,0.08)] ' +
  'hover:text-[#37352F] dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white transition-colors';

interface AttachmentFileRowProps {
  attachment: AttachmentLike;
  /** Riga secondaria extra (es. "Nota del 12/09/2026") */
  subtitle?: string;
  /** Se presente mostra il pulsante "Vai alla nota" */
  onGoToNote?: () => void;
}

export const AttachmentFileRow: React.FC<AttachmentFileRowProps> = ({ attachment, subtitle, onGoToNote }) => {
  const kind = getAttachmentKind(attachment.name, attachment.type);
  const { Icon, cls } = KIND_STYLE[kind];
  const ext = attachmentExtension(attachment.name).toUpperCase();
  const size = formatAttachmentSize(attachment.size);
  const meta = [ext, size, subtitle].filter(Boolean).join(' · ');

  return (
    <SignedUrl url={attachment.url}>
      {(href) => {
        const openUrl = href ?? attachment.url;
        const downloadUrl = withDownloadParam(openUrl, attachment.name) ?? undefined;
        return (
          <div className="flex items-center gap-2.5 rounded-md border border-[rgba(55,53,47,0.12)] dark:border-[#3F4447] bg-white dark:bg-[#2F3437] px-2 py-1.5">
            <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded ${cls}`}>
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <a
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1"
              {...tip(attachment.name)}
            >
              <span className="block truncate text-[13px] font-medium text-[#37352F] dark:text-white hover:underline">
                {attachment.name}
              </span>
              {meta && (
                <span className="block truncate text-[11px] text-[#9B9A97] dark:text-white/50">{meta}</span>
              )}
            </a>
            <div className="flex flex-shrink-0 items-center">
              {onGoToNote && (
                <button type="button" onClick={onGoToNote} className={ACTION_CLS} {...iconTip('Vai alla nota')}>
                  <CornerDownRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              )}
              <a href={openUrl} target="_blank" rel="noopener noreferrer" className={ACTION_CLS} {...iconTip('Apri')}>
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
              </a>
              <a href={downloadUrl} download={attachment.name} className={ACTION_CLS} {...iconTip('Scarica')}>
                <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
              </a>
            </div>
          </div>
        );
      }}
    </SignedUrl>
  );
};
